import { useEffect, useState, useCallback, useRef } from "react";
import { usePlaybackTime } from "../context/PlaybackTimeContext";
import { TranscriptionReader } from "../types/transcription/Transcription";

interface UseTranscriptionAutoScrollProps {
  reader: TranscriptionReader | undefined;
  containerRef: React.RefObject<HTMLDivElement>;
}

interface UseTranscriptionAutoScrollResult {
  currentBlockId: number | null;
  lastPlayedBlockId: number | null;
  scrollToBlock: (blockRef: React.RefObject<HTMLDivElement>) => void;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

export function useTranscriptionAutoScroll({
  reader,
  containerRef,
}: UseTranscriptionAutoScrollProps): UseTranscriptionAutoScrollResult {
  const { currentTimeSeconds } = usePlaybackTime();
  const [currentBlockId, setCurrentBlockId] = useState<number | null>(null);
  const [lastPlayedBlockId, setLastPlayedBlockId] = useState<number | null>(
    null
  );
  const [enabled, setEnabled] = useState(true);
  const prevEnabledRef = useRef(enabled);

  useEffect(() => {
    if (!reader || currentTimeSeconds === undefined) {
      setCurrentBlockId(null);
      return;
    }

    const block = reader.getBlockAtTimeSeconds(currentTimeSeconds);
    if (block) {
      setCurrentBlockId(block.id);
      setLastPlayedBlockId(block.id);
    } else {
      setCurrentBlockId(null);
    }
  }, [reader, currentTimeSeconds]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const handleScroll = () => {
      setEnabled(false);
    };

    container.addEventListener("wheel", handleScroll, { passive: true });
    container.addEventListener("touchmove", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("wheel", handleScroll);
      container.removeEventListener("touchmove", handleScroll);
    };
  }, [containerRef, enabled]);

  const scrollToBlockById = useCallback(
    (blockId: number) => {
      const container = containerRef.current;
      if (!container) return;

      const blockElement = container.querySelector(
        `[data-block-id="${blockId}"]`
      );
      if (!blockElement) return;

      blockElement.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    [containerRef]
  );

  const scrollToBlock = useCallback(
    (blockRef: React.RefObject<HTMLDivElement>) => {
      const block = blockRef.current;
      if (!block) return;

      block.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    []
  );

  // When auto-scroll is re-enabled, immediately scroll to the current position
  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      // Just transitioned from disabled to enabled
      const blockId = currentBlockId ?? lastPlayedBlockId;
      if (blockId !== null) {
        scrollToBlockById(blockId);
      }
    }
    prevEnabledRef.current = enabled;
  }, [enabled, currentBlockId, lastPlayedBlockId, scrollToBlockById]);

  return {
    currentBlockId,
    lastPlayedBlockId,
    scrollToBlock,
    enabled,
    setEnabled,
  };
}
